/**
 * Growth Intelligence UI kit — shared primitives for every Marketing → Growth page.
 *
 * One visual language: near-black / slate surfaces carry the hierarchy, teal is
 * reserved for the primary action and brand emphasis, and semantic colours
 * (emerald / amber / rose) only ever mean positive / caution / negative. Every
 * number keeps its basis label (ACTUAL · MODELLED · ATTRIBUTED · UNATTRIBUTED ·
 * ESTIMATED). Built on the existing Tailwind conventions, Public Sans / Manrope
 * typography and lucide icons — no new dependencies.
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, ChevronRight, Loader2, Menu, RefreshCw, X } from 'lucide-react';
import Sidebar from '../Sidebar';
import { useBranding } from '../../context/BrandingContext';
import { GROWTH_NAV, isGrowthPageDeployed, visibleGrowthNavItems } from '../../utils/growthEngineGate';

// Re-exported so existing importers keep working; the definition lives with the gate that filters it.
export { GROWTH_NAV };
import './engine.css';

// ─── formatting ────────────────────────────────────────────────────────────
export const fmt = (n) => (n === null || n === undefined ? '—' : typeof n === 'number' ? n.toLocaleString() : Number.isFinite(Number(n)) && String(n).trim() !== '' ? Number(n).toLocaleString() : String(n));
export const money = (n, ccy) => (n === null || n === undefined ? '—' : `${ccy ? `${ccy} ` : ''}${Number(n).toLocaleString()}`);
export const pct = (v, digits = 0) => (v === null || v === undefined ? '—' : `${(Number(v) * 100).toFixed(digits)}%`);
export const pctChange = (c) => (c === null || c === undefined ? '' : `${c > 0 ? '+' : ''}${Math.round(c * 100)}%`);
export const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—');
export const day = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '—');
export const greeting = (name) => {
  const h = new Date().getHours();
  const part = h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
  return name ? `${part}, ${name}` : part;
};

// ─── tones ─────────────────────────────────────────────────────────────────
const TONES = {
  slate: 'bg-slate-100 text-slate-700 border-slate-200',
  dark: 'bg-slate-900 text-white border-slate-900',
  teal: 'bg-teal-50 text-teal-800 border-teal-200',
  amber: 'bg-amber-50 text-amber-800 border-amber-200',
  emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  rose: 'bg-rose-50 text-rose-800 border-rose-200',
  violet: 'bg-violet-50 text-violet-800 border-violet-200',
};

/** Small uppercase tag. Same signature as the old per-page Chip. */
export function Chip({ children, tone = 'slate', title, className = '' }) {
  return <span title={title} className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${TONES[tone] || TONES.slate} ${className}`}>{children}</span>;
}

/** Basis label — the one thing every number must carry. */
const BASIS = {
  actual: ['emerald', 'ACTUAL', 'Counted from records'],
  counted: ['emerald', 'COUNTED', 'Counted from records'],
  modelled: ['amber', 'MODELLED', 'Read from pipeline stages or website signals — not counted'],
  estimated: ['amber', 'ESTIMATED', 'An estimate, not a measurement'],
  attributed: ['teal', 'ATTRIBUTED', 'Carries its own first visit (deterministic)'],
  unattributed: ['slate', 'UNATTRIBUTED', 'Nothing recorded about where it came from'],
  'modelled source': ['amber', 'MODELLED SOURCE', 'Last touch or CRM source'],
  'not measured': ['slate', 'NOT MEASURED', 'No source connected for this yet'],
};
export function Basis({ kind, className = '' }) {
  const key = String(kind || '').toLowerCase();
  const [tone, label, title] = BASIS[key] || ['slate', String(kind || '').toUpperCase(), ''];
  return <Chip tone={tone} title={title} className={className}>{label}</Chip>;
}

const CONF = { high: ['emerald', 'we are fairly sure'], medium: ['teal', 'reasonably sure'], low: ['slate', 'too little to be sure'] };
export function Confidence({ level }) {
  const [tone, label] = CONF[level] || CONF.low;
  return <Chip tone={tone}>{label}</Chip>;
}

/** AUTO · INFORM · APPROVAL REQUIRED — derived from the agent level (0–4). */
export const AUTONOMY = {
  auto: ['emerald', 'Auto', 'Done on its own, within policy'],
  inform: ['teal', 'Inform', 'Done, and you are told'],
  approval: ['amber', 'Approval required', 'Waits for a person'],
};
export function autonomyOf(level) {
  const l = Number(level);
  if (Number.isNaN(l)) return 'inform';
  if (l <= 2) return 'auto';
  if (l === 3) return 'inform';
  return 'approval';
}
export function Autonomy({ level, mode }) {
  const [tone, label, title] = AUTONOMY[mode || autonomyOf(level)];
  return <Chip tone={tone} title={title}>{label}</Chip>;
}

/** Signed change with colour that never carries meaning alone (arrow + sign). */
export function Delta({ value, suffix = '', invert = false, className = '' }) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return <span className={`text-xs text-slate-400 ${className}`}>no prior data</span>;
  const v = Number(value);
  const good = invert ? v < 0 : v > 0;
  const tone = v === 0 ? 'text-slate-500' : good ? 'text-emerald-700' : 'text-rose-700';
  const arrow = v === 0 ? '→' : v > 0 ? '↑' : '↓';
  return <span className={`text-xs font-semibold ${tone} ${className}`}>{arrow} {v > 0 ? '+' : ''}{Math.round(v * 100)}%{suffix}</span>;
}

// ─── surfaces ──────────────────────────────────────────────────────────────
/** Metric tile. `highlight` = the one number that matters on the page (teal). */
export function Kpi({ label, value, sub, highlight = false, delta, link, basis, invert }) {
  const inner = (
    <div className={`rounded-xl border p-3.5 h-full transition ${highlight ? 'border-teal-600/40 bg-white shadow-[inset_3px_0_0_0_#0d9488]' : 'border-slate-200 bg-white'} ${link ? 'hover:border-slate-400' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</div>
        {basis && <Basis kind={basis} />}
      </div>
      <div className={`text-2xl font-bold mt-1 tracking-tight ${highlight ? 'text-teal-700' : 'text-slate-900'}`}>{typeof value === 'string' || typeof value === 'number' ? fmt(value) : value}</div>
      {(delta !== undefined || sub !== undefined) && (
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-500">
          {delta !== undefined && <Delta value={delta} invert={invert} />}
          {sub !== undefined && <span>{sub}</span>}
        </div>
      )}
    </div>
  );
  return link ? <Link to={link} className="block h-full">{inner}</Link> : inner;
}
export const Stat = Kpi;
export const Tile = Kpi;

/** Card with a header. Same signature as the old per-page Panel. */
export function Panel({ title, icon: Icon, children, footer, right, tone = 'white', className = '' }) {
  const surface = tone === 'dark' ? 'bg-slate-900 text-slate-100 border-slate-800' : 'bg-white border-slate-200';
  const head = tone === 'dark' ? 'border-slate-800' : 'border-slate-100';
  return (
    <section className={`${surface} border rounded-2xl overflow-hidden ${className}`}>
      {(title || right) && (
        <div className={`px-5 py-3.5 border-b ${head} flex items-center gap-2`}>
          {Icon && <Icon size={16} className={tone === 'dark' ? 'text-teal-300' : 'text-slate-500'} />}
          <h2 className={`font-semibold text-[16px] ${tone === 'dark' ? 'text-white' : 'text-slate-900'}`}>{title}</h2>
          {right && <div className={`ml-auto text-xs ${tone === 'dark' ? 'text-slate-300' : 'text-slate-500'}`}>{right}</div>}
        </div>
      )}
      <div className="p-5">{children}</div>
      {footer && <div className={`px-5 py-2.5 text-[11px] leading-relaxed border-t ${head} ${tone === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>{footer}</div>}
    </section>
  );
}

/** Section heading: numbered question the founder asks, e.g. "What changed?" */
export function Question({ n, children, right }) {
  return (
    <div className="flex items-center gap-3">
      {n !== undefined && <span className="w-6 h-6 rounded-md bg-slate-900 text-white text-[11px] font-bold flex items-center justify-center">{n}</span>}
      <h2 className="text-base font-bold text-slate-900 tracking-tight">{children}</h2>
      {right && <div className="ml-auto text-xs text-slate-500">{right}</div>}
    </div>
  );
}

export function Empty({ children, className = '' }) {
  return <div className={`rounded-xl border border-dashed border-slate-200 bg-white/60 px-4 py-8 text-sm text-slate-500 text-center ${className}`}>{children}</div>;
}

export function Spinner() {
  return <div className="flex justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-slate-400" /></div>;
}

/** Segmented control (windows, views). */
export function Segmented({ options, value, onChange, size = 'sm' }) {
  return (
    <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" role="tablist">
      {options.map((o) => (
        <button key={o.value} role="tab" aria-selected={value === o.value} onClick={() => onChange(o.value)}
          className={`px-3 ${size === 'sm' ? 'py-1 text-xs' : 'py-1.5 text-sm'} rounded-md font-medium transition ${value === o.value ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>{o.label}</button>
      ))}
    </div>
  );
}

export function Button({ children, variant = 'secondary', size = 'sm', className = '', ...props }) {
  const base = 'inline-flex items-center gap-1.5 rounded-lg font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500';
  const sz = size === 'xs' ? 'px-2.5 py-1 text-xs' : size === 'md' ? 'px-4 py-2 text-sm' : 'px-3 py-1.5 text-sm';
  const v = {
    primary: 'bg-teal-600 text-white hover:bg-teal-700',
    dark: 'bg-slate-900 text-white hover:bg-slate-800',
    secondary: 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50',
    ghost: 'text-slate-600 hover:bg-slate-100',
    danger: 'bg-rose-600 text-white hover:bg-rose-700',
  }[variant];
  return <button className={`${base} ${sz} ${v} ${className}`} {...props}>{children}</button>;
}

/** Growth Engine entitlement, mirrored from the server's branding payload (features.growth_engine). Hidden ⇒ the API also denies. */
export function useGrowthEngineEnabled() {
  const branding = useBranding();
  return branding?.features?.growth_engine === true;
}
/** Route guard: a non-entitled tenant opening a Growth Engine URL directly is sent to Visitors — the server would 403 anyway. */
export function GrowthEngineRoute({ children }) {
  const enabled = useGrowthEngineEnabled();
  const branding = useBranding();
  if (branding?.features === undefined && !branding?.slug) return null;   // branding not loaded yet
  if (!enabled) return <NotEnabled />;
  return children;
}
function NotEnabled() {
  return (
    <div className="dashboard-layout growth-shell"><Sidebar /><div className="main-content-bare overflow-y-auto"><div className="p-8 max-w-xl space-y-3">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Growth Engine</div>
      <h1 className="text-xl font-bold text-slate-900">Not enabled for this workspace</h1>
      <p className="text-sm text-slate-600">Growth Engine is currently available to Euphoric AI Internal only. Nothing here is available to this workspace, and the server refuses its data. Marketing → Visitors, Leads, Automation and the other marketing pages continue to work as before.</p>
      <Link to="/admin/marketing/visitors" className="inline-block text-sm font-semibold text-teal-700">Go to Visitors →</Link>
    </div></div></div>
  );
}

function GrowthNav() {
  const { pathname, search } = useLocation();
  const enabled = useGrowthEngineEnabled();
  // Deployed AND entitled. A build that carries one Growth module must not advertise the rest.
  const groups = GROWTH_NAV.map((g) => ({ ...g, items: visibleGrowthNavItems(g.items, enabled) })).filter((g) => g.items.length);
  return (
    <nav aria-label="Growth Engine areas" className="flex items-center gap-1 overflow-x-auto no-scrollbar -mx-1 px-1">
      {groups.map((g, gi) => (
        <React.Fragment key={g.group}>
          {gi > 0 && <span className="w-px h-4 bg-slate-200 mx-1 flex-shrink-0" aria-hidden />}
          {g.items.map((it) => {
            const [p, q] = it.path.split('?');
            const active = pathname === p && (!q || search.includes(q)) && !(p === '/admin/marketing/visitors' && !q && search.includes('view=intent'));
            return <Link key={it.path} to={it.path} className={`whitespace-nowrap px-2.5 py-1 rounded-md text-xs font-medium transition ${active ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}>{it.label}</Link>;
          })}
        </React.Fragment>
      ))}
    </nav>
  );
}

/**
 * Page shell for every growth page: sidebar, "Growth Intelligence" eyebrow with
 * the area nav, title, plain-English intro, right-hand controls.
 */
export function GrowthPage({ title, icon: Icon, intro, controls, children, onRefresh, asOf, wide = false }) {
  const [menu, setMenu] = React.useState(false);
  const engine = useGrowthEngineEnabled();
  const hub = engine && isGrowthPageDeployed('/admin/marketing/growth') ? '/admin/marketing/growth'
    : isGrowthPageDeployed('/admin/marketing/visitors') ? '/admin/marketing/visitors' : null;
  return (
    <div className={`dashboard-layout growth-shell ${menu ? 'growth-shell--menu' : ''}`}>
      <Sidebar />
      <div className="main-content-bare overflow-y-auto ge" data-ge-theme="dark">
        {/* Phone / tablet bar — the fixed sidebar is hidden under 1024px on growth pages; this button brings it back. */}
        <div className="growth-mobile-bar lg:hidden sticky top-0 z-30 flex items-center gap-2 px-4 py-2.5 bg-white/95 backdrop-blur border-b border-slate-200">
          <button onClick={() => setMenu((v) => !v)} aria-label={menu ? 'Close menu' : 'Open menu'} aria-expanded={menu} className="p-2 -ml-2 rounded-lg hover:bg-slate-100 text-slate-700">{menu ? <X size={18} /> : <Menu size={18} />}</button>
          <span className="text-sm font-semibold text-slate-900 truncate">{title}</span>
          {engine && hub === '/admin/marketing/growth' && <Link to={hub} className="ml-auto text-xs font-medium text-slate-600">Command Center</Link>}
          {engine && <EngineState compact />}
        </div>
        <div className={`p-4 sm:p-6 space-y-5 ${wide ? '' : 'max-w-[1440px]'}`}>
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              {hub ? <Link to={hub} className="hover:text-slate-900">{engine ? 'Growth Engine' : 'Growth Intelligence'}</Link>
                   : <span>{engine ? 'Growth Engine' : 'Growth Intelligence'}</span>}
              <ChevronRight size={12} aria-hidden />
              <span className="text-slate-900">{title}</span>
            </div>
            <GrowthNav />
            <div className="flex flex-wrap items-start justify-between gap-3 pt-1">
              <div className="min-w-0">
                <h1 className="text-[22px] sm:text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">{Icon && <Icon className="text-slate-700" size={22} aria-hidden />} {title}</h1>
                {intro && <p className="text-sm text-slate-600 mt-1 max-w-3xl">{intro}</p>}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {engine && <EngineState />}
                {controls}
                {asOf && <span className="text-xs text-slate-500">as of {when(asOf)}</span>}
                {onRefresh && <button onClick={onRefresh} className="p-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-600" title="Refresh" aria-label="Refresh"><RefreshCw size={15} /></button>}
              </div>
            </div>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

export function OpenLink({ to, children = 'Open' }) {
  return <Link to={to} className="text-xs font-medium text-slate-700 hover:text-teal-700 inline-flex items-center gap-1">{children} <ArrowRight size={12} aria-hidden /></Link>;
}

/** Simple horizontal bar list (label · value · bar) — no chart library needed. */
export function Bars({ rows, labelKey = 'label', valueKey = 'value', format = fmt, max, mono = false, empty = 'No data in this window.', onClick }) {
  if (!rows || rows.length === 0) return <Empty>{empty}</Empty>;
  const top = max || Math.max(...rows.map((r) => Number(r[valueKey]) || 0), 1);
  return (
    <ul className="space-y-2">
      {rows.map((r, i) => (
        <li key={i} className={onClick ? 'cursor-pointer group' : ''} onClick={onClick ? () => onClick(r) : undefined}>
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className={`truncate ${mono ? 'font-mono text-[12px]' : ''} text-slate-800 group-hover:text-teal-700`}>{r[labelKey]}</span>
            <span className="text-slate-900 font-semibold tabular-nums">{format(r[valueKey])}</span>
          </div>
          <div className="h-1.5 rounded-full bg-slate-100 mt-1 overflow-hidden"><div className="h-full rounded-full bg-slate-800" style={{ width: `${Math.max(2, (Number(r[valueKey]) || 0) / top * 100)}%` }} /></div>
        </li>
      ))}
    </ul>
  );
}


// ─── Growth Engine V1 primitives (semantic tokens; presentation only) ───────

/** The live engine state word + autonomy, read from /engine/brain. Shown in every Growth Engine header. Never invents a state. */
export function EngineState({ compact = false }) {
  const [b, setB] = React.useState(null);
  React.useEffect(() => {
    if (!isGrowthPageDeployed('/admin/marketing/brain')) return undefined;   // no orchestrator in this build
    let alive = true;
    let t;
    import('../../utils/api').then(({ engineAPI }) => {
      if (!alive || !engineAPI) return;
      const read = () => engineAPI.brain().then((r) => alive && setB(r.data)).catch(() => alive && setB({ error: true }));
      read();
      t = setInterval(read, 60000);
    });
    return () => { alive = false; if (t) clearInterval(t); };
  }, []);
  if (!b || b.error) return null;
  const e = b.engine || {};
  const word = e.state_word || 'IDLE';
  const live = e.state === 'running';
  return (
    <Link to="/admin/marketing/brain" title="Growth Engine state (counted)" className="inline-flex items-center gap-2 rounded-lg px-2.5 py-1.5 ge-surface-2 text-xs">
      <span className={`w-2 h-2 rounded-full ${live ? 'animate-[ge-pulse_2.4s_ease-in-out_infinite]' : ''}`} style={{ background: live ? 'var(--ge-intelligence)' : word === 'STOPPED' ? 'var(--ge-critical)' : 'var(--ge-text-3)' }} />
      <span className="font-bold tracking-[.12em] text-[10px]" style={{ color: live ? 'var(--ge-intelligence-ink)' : 'var(--ge-text-2)' }}>{word}</span>
      {!compact && <span className="ge-tab" style={{ color: 'var(--ge-text-3)' }}>{fmt(e.discover?.open_opportunities)} open · agent {e.agent?.enabled ? 'on' : 'off'}</span>}
    </Link>
  );
}

/** System status language: OBSERVING · ANALYZING · PRIORITIZING · ACTION READY · AWAITING APPROVAL · AGENT EXECUTING · ACTION COMPLETED · LEARNING · IDLE · STOPPED */
const STATE_TONE = { OBSERVING: 'teal', ANALYZING: 'teal', PRIORITIZING: 'teal', 'ACTION READY': 'emerald', 'AWAITING APPROVAL': 'amber', 'AGENT EXECUTING': 'teal', 'ACTION COMPLETED': 'emerald', LEARNING: 'teal', IDLE: 'slate', STOPPED: 'rose', HELD: 'slate', BLOCKED: 'slate', FAILED: 'rose' };
export function StateTag({ state }) {
  const w = String(state || '').toUpperCase().replace(/_/g, ' ');
  return <Chip tone={STATE_TONE[w] || 'slate'}>{w || '—'}</Chip>;
}
/** agent_action result / approval → status language */
export function actionState(row) {
  if (!row) return 'IDLE';
  if (row.approval === 'pending' || row.result === 'queued') return 'AWAITING APPROVAL';
  if (row.result === 'sent') return 'ACTION COMPLETED';
  if (row.result === 'blocked') return 'HELD';
  if (row.result === 'failed') return 'FAILED';
  if (row.result === 'dry_run') return 'ACTION READY';
  return String(row.result || row.status || 'IDLE');
}

/** Provenance line: source · basis · last updated · confidence — first-class on every intelligence surface. */
export function Provenance({ source, basis, updated, confidence, className = '' }) {
  return (
    <div className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] ${className}`} style={{ color: 'var(--ge-text-3)' }}>
      {source && <span>source: <span style={{ color: 'var(--ge-text-2)' }}>{source}</span></span>}
      {basis && <Basis kind={basis} />}
      {updated && <span>updated {when(updated)}</span>}
      {confidence && <Confidence level={confidence} />}
    </div>
  );
}

/** Loading steps bound to the requests actually in flight — never a fake percentage. */
export function LoadingSteps({ steps }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 py-6" aria-live="polite" aria-busy="true">
      {steps.map((s) => (
        <div key={s.label} className="ge-surface-2 rounded-xl p-3.5 space-y-2.5">
          <div className="flex items-center justify-between text-xs"><span style={{ color: 'var(--ge-text)' }}>{s.label}</span><span className="ge-tab" style={{ color: 'var(--ge-text-3)' }}>{s.done ? 'done' : s.failed ? 'no answer' : 'working…'}</span></div>
          <div className="h-0.5 rounded-full overflow-hidden" style={{ background: 'var(--ge-surface-3)' }}><div className={`h-full ${s.done || s.failed ? '' : 'animate-[ge-pulse_1.6s_ease-in-out_infinite]'}`} style={{ width: s.done ? '100%' : s.failed ? '100%' : '40%', background: s.failed ? 'var(--ge-critical)' : 'var(--ge-intelligence)' }} /></div>
          {!s.done && <><div className="ge-skeleton h-3 w-3/4" /><div className="ge-skeleton h-3 w-1/2" /></>}
        </div>
      ))}
    </div>
  );
}

export function ErrorState({ title = 'This source did not answer', detail, onRetry }) {
  return (
    <div className="rounded-xl p-4 text-sm" style={{ border: '1px solid color-mix(in srgb, var(--ge-critical) 40%, transparent)', background: 'color-mix(in srgb, var(--ge-critical) 8%, transparent)' }}>
      <div className="font-semibold" style={{ color: 'var(--ge-critical)' }}>{title}</div>
      {detail && <div className="mt-1 text-xs" style={{ color: 'var(--ge-text-2)' }}>{detail}</div>}
      <div className="mt-1 text-xs" style={{ color: 'var(--ge-text-3)' }}>Nothing was estimated in its place.</div>
      {onRetry && <Button className="mt-3" onClick={onRetry}>Retry</Button>}
    </div>
  );
}

/**
 * External source state chip.
 *
 * connected          the provider answered and works
 * credential_present a key is set but has never been verified — amber, never green
 * credential_rejected / payment_required / unreachable  the provider said why it will not serve us
 * not_connected · not_measured · error · rate_limited
 *
 * Only `connected` is green. A credential that exists is not a credential that works, and the chip
 * must not imply otherwise.
 */
export function SourceState({ state }) {
  const tone = {
    connected: 'emerald',
    credential_present: 'amber',
    credential_rejected: 'rose', payment_required: 'rose', unreachable: 'rose',
    not_connected: 'slate', not_measured: 'slate', error: 'rose', rate_limited: 'amber',
  }[state] || 'slate';
  return <Chip tone={tone}>{String(state || 'unknown').replace(/_/g, ' ')}</Chip>;
}

/** Large tabular number with its basis — the one honest way to show a metric here. */
export function Num({ label, value, basis, sub, highlight = false }) {
  return (
    <div className="flex flex-col gap-1.5 min-w-0">
      <div className="ge-eyebrow">{label}</div>
      <div className="ge-tab text-[30px] leading-none font-bold tracking-tight" style={{ color: highlight ? 'var(--ge-intelligence-ink)' : 'var(--ge-text)' }}>{value === null || value === undefined ? '—' : fmt(value)}</div>
      <div className="flex items-center gap-2 text-[11px]" style={{ color: 'var(--ge-text-3)' }}>{basis && <Basis kind={basis} />}{sub}</div>
    </div>
  );
}

/** Ruled section with an eyebrow label (no card) */
export function Rule({ label, right, children, className = '' }) {
  return (
    <section className={`space-y-3 ${className}`}>
      <div className="flex items-baseline justify-between gap-3"><div className="ge-eyebrow">{label}</div>{right && <div className="text-xs" style={{ color: 'var(--ge-text-3)' }}>{right}</div>}</div>
      <div className="ge-rule" />
      {children}
    </section>
  );
}
